import { forwardRef, useEffect, useState } from 'react';
import { SparkIcon } from '../SparkIcon';
import { Label } from '../Label/Label';
import type { InputSize, InputState, InputFill, InputProps } from './Input.types';
import './Input.css';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({
  label = 'Label',
  helperText = 'Helper text',
  placeholder = 'Input',
  size = 'medium',
  fill,
  danger = false,
  disabled = false,
  showLabel = true,
  showHelperText = false,
  showAsterisk = false,
  showHelpIcon = false,
  showPrefixIcon = false,
  prefixIconSlug = 'user',
  prefixText,
  showInfoIcon = false,
  suffixIconSlug,
  onSuffixIconClick,
  suffixIconAriaLabel,
  showCharCount = false,
  charCount = 0,
  maxChars = 40,
  visualState,
  showFocus = false,
  className = '',
  id,
  value,
  defaultValue,
  onChange,
  ...rest
}, ref) {
  const [internalValue, setInternalValue] = useState(
    (value as string | undefined) ?? (defaultValue as string | undefined) ?? '',
  );

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value as string);
    }
  }, [value]);

  const inputId = id ?? `spark-input-${Math.random().toString(36).slice(2, 9)}`;
  const resolvedState: InputState = disabled
    ? 'disabled'
    : visualState ?? (danger ? 'danger' : 'default');
  const isFocused = showFocus || resolvedState === 'click-on';
  const currentValue = value !== undefined ? String(value) : internalValue;
  const resolvedFill: InputFill = fill ?? (currentValue.length > 0 ? 'filled' : 'empty');
  const resolvedCharCount = charCount ?? currentValue.length;

  const rootClasses = [
    'spark-input',
    `spark-input-${size}`,
    `spark-input-state-${resolvedState}`,
    `spark-input-fill-${resolvedFill}`,
    danger ? 'spark-input-danger' : '',
    disabled ? 'spark-input-disabled' : '',
    isFocused ? 'spark-input-show-focus' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      {showLabel ? (
        <Label
          label={label}
          htmlFor={inputId}
          showAsterisk={showAsterisk}
          showIcon={showHelpIcon}
        />
      ) : null}
      <div className="spark-input-field-wrap">
        <div className="spark-input-field spark-input-anatomy">
          {showPrefixIcon ? <SparkIcon slug={prefixIconSlug} size="small" className="spark-input-prefix-icon" /> : null}
          {prefixText != null && prefixText !== false ? (
            <span className="spark-input-prefix-text" aria-hidden="true">
              {prefixText}
            </span>
          ) : null}
          <input
            ref={ref}
            id={inputId}
            className="spark-input-control"
            placeholder={placeholder}
            disabled={disabled}
            value={value !== undefined ? value : internalValue}
            onChange={(event) => {
              if (value === undefined) {
                setInternalValue(event.target.value);
              }
              onChange?.(event);
            }}
            aria-invalid={danger || undefined}
            {...rest}
          />
          {showCharCount ? (
            <span className="spark-input-char-count" aria-hidden="true">
              {resolvedCharCount} / {maxChars}
            </span>
          ) : null}
          {suffixIconSlug ? (
            onSuffixIconClick ? (
              <button
                type="button"
                className="spark-input-suffix-button"
                aria-label={suffixIconAriaLabel}
                onClick={onSuffixIconClick}
                disabled={disabled}
              >
                <SparkIcon slug={suffixIconSlug} size="small" />
              </button>
            ) : (
              <SparkIcon slug={suffixIconSlug} size="small" className="spark-input-suffix-icon" />
            )
          ) : null}
          {showInfoIcon ? <SparkIcon slug="circle-info" size="small" className="spark-input-info-icon" /> : null}
        </div>
        <span className="spark-input-focus" aria-hidden="true" />
      </div>
      {showHelperText ? <span className="spark-input-helper">{helperText}</span> : null}
    </div>
  );
});

Input.displayName = 'Input';
